import React, { useEffect, useRef, useState } from "react";
import { Badge, Box, ButtonBase, CircularProgress, Tooltip, alpha, useTheme } from "@mui/material";
import HandymanRoundedIcon from "@mui/icons-material/HandymanRounded";
import { useTranslation } from "react-i18next";
import AppIconButton from "../../../components/form/AppIconButton";

export type ChatToolsPaletteItem = {
  key: string;
  icon: React.ReactNode;
  label: string;
  hint?: string;
  onSelect: () => void;
  /** Highlights the tile (e.g. something is already selected for this tool). */
  active?: boolean;
  busy?: boolean;
  disabled?: boolean;
};

interface Props {
  items: ChatToolsPaletteItem[];
  disabled?: boolean;
  iconColor: string;
  /** Number of active selections, shown as a badge on the trigger. */
  activeCount?: number;
  /** Rendered next to the trigger; picker panels anchored here open above the button. */
  children?: React.ReactNode;
}

/** Arc radius (px) from the trigger centre to each tool's centre. */
const RADIUS = 104;
const ITEM_SIZE = 42;
/** Arc span in degrees, measured clockwise from straight up (0°) towards the right (90°). */
const ARC_START = -6;
const ARC_END = 96;

/** Single composer "tools" button that fans the chat tools out on a circular arc. */
export default function ChatToolsPalette({ items, disabled, iconColor, activeCount = 0, children }: Props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (event.target instanceof Node && rootRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const primary = theme.palette.primary.main;
  const step = items.length > 1 ? (ARC_END - ARC_START) / (items.length - 1) : 0;
  const ringOuter = RADIUS + ITEM_SIZE / 2 + 8;
  const ringInner = RADIUS - ITEM_SIZE / 2 - 8;
  const spring = "cubic-bezier(.34,1.56,.64,1)";

  return (
    <Box ref={rootRef} sx={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
      {/* Arc band behind the tools; sweeps open like a fan from the trigger. */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          left: "50%",
          top: "50%",
          width: ringOuter * 2,
          height: ringOuter * 2,
          ml: `${-ringOuter}px`,
          mt: `${-ringOuter}px`,
          borderRadius: "50%",
          pointerEvents: "none",
          zIndex: theme.zIndex.modal - 1,
          background: `conic-gradient(from ${ARC_START - 10}deg, ${alpha(primary, 0.14)} 0deg ${
            ARC_END - ARC_START + 20
          }deg, transparent ${ARC_END - ARC_START + 20}deg)`,
          mask: `radial-gradient(circle, transparent ${ringInner}px, #000 ${ringInner + 1}px)`,
          WebkitMask: `radial-gradient(circle, transparent ${ringInner}px, #000 ${ringInner + 1}px)`,
          backdropFilter: "blur(6px)",
          opacity: open ? 1 : 0,
          transform: open ? "scale(1) rotate(0deg)" : "scale(0.35) rotate(-70deg)",
          transition: `transform 380ms ${spring}, opacity 220ms ease`,
          "@media (prefers-reduced-motion: reduce)": { transition: "opacity 120ms" },
        }}
      />

      {items.map((item, i) => {
        const angle = ((ARC_START + i * step) * Math.PI) / 180;
        const x = RADIUS * Math.sin(angle);
        const y = -RADIUS * Math.cos(angle);
        const delay = open ? 50 + i * 45 : (items.length - 1 - i) * 30;
        return (
          <Tooltip key={item.key} title={item.hint ? `${item.label} — ${item.hint}` : item.label} placement="top" arrow>
            <Box
              component="span"
              sx={{
                position: "absolute",
                left: "50%",
                top: "50%",
                width: ITEM_SIZE,
                height: ITEM_SIZE,
                ml: `${-ITEM_SIZE / 2}px`,
                mt: `${-ITEM_SIZE / 2}px`,
                zIndex: theme.zIndex.modal,
                pointerEvents: open ? "auto" : "none",
                opacity: open ? 1 : 0,
                transform: open
                  ? `translate(${x}px, ${y}px) scale(1) rotate(0deg)`
                  : "translate(0, 0) scale(0.2) rotate(-120deg)",
                transition: `transform 420ms ${spring} ${delay}ms, opacity 200ms ease ${delay}ms`,
                "@media (prefers-reduced-motion: reduce)": { transition: "opacity 120ms" },
              }}
            >
              <ButtonBase
                role="menuitem"
                aria-label={item.label}
                tabIndex={open ? 0 : -1}
                disabled={item.disabled || item.busy}
                onClick={() => {
                  setOpen(false);
                  item.onSelect();
                }}
                sx={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  color: item.active ? theme.palette.getContrastText(primary) : primary,
                  bgcolor: item.active ? primary : theme.palette.background.paper,
                  border: `1.5px solid ${alpha(primary, item.active ? 0 : 0.35)}`,
                  boxShadow: `0 4px 14px ${alpha(primary, 0.28)}`,
                  fontWeight: 700,
                  transition: "transform 180ms, box-shadow 180ms",
                  "&:hover": { transform: "scale(1.14)", boxShadow: `0 6px 18px ${alpha(primary, 0.4)}` },
                  "&.Mui-disabled": { opacity: 0.45 },
                }}
              >
                {item.busy ? <CircularProgress size={16} /> : item.icon}
              </ButtonBase>
            </Box>
          </Tooltip>
        );
      })}

      <Badge
        color="primary"
        badgeContent={activeCount}
        invisible={activeCount === 0 || open}
        overlap="circular"
        sx={{
          zIndex: open ? theme.zIndex.modal + 1 : undefined,
          "& .MuiBadge-badge": { fontSize: "0.62rem", height: 16, minWidth: 16, px: 0.5 },
        }}
      >
        <AppIconButton
          size="small"
          busyIgnore
          disabled={disabled}
          title={t("chat.tools.open")}
          aria-label={t("chat.tools.open")}
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
          sx={{
            width: { xs: 44, sm: 36 },
            height: { xs: 44, sm: 36 },
            color: open ? theme.palette.getContrastText(primary) : activeCount > 0 ? primary : iconColor,
            bgcolor: open ? primary : undefined,
            "&:hover": open ? { bgcolor: primary } : undefined,
            transition: `transform 320ms ${spring}, background-color 200ms, color 200ms`,
            transform: open ? "rotate(-45deg) scale(1.08)" : "none",
          }}
        >
          <HandymanRoundedIcon fontSize="small" />
        </AppIconButton>
      </Badge>
      {children}
    </Box>
  );
}
